import { createSelector } from 'reselect';
import { State } from 'reducers';
import { GroupId, CallId, DeviceId } from 'shared/models';

export type HasCallId = {
    callId: CallId;
};

export type HasGatewayDeviceId = {
    gatewayDeviceId: DeviceId;
};

export type HasElevatorId = {
    elevatorId: GroupId;
};

export type HasExternalId = {
    externalId?: string;
};

const getCallId = (state: any, { callId }: Partial<HasCallId>) => callId;

const getElevatorId = (state: any, { elevatorId }: Partial<HasElevatorId>) => elevatorId;

const getFramesParameter = (state: any, { frames = false }: { frames?: boolean }) => !!frames;


const getEcsState =
    (state: State) => state.ecs;

export const getElevatorVideoCallId =
    createSelector(
        getEcsState,
        getElevatorId,
        (state, elevatorId) => {
            if (!elevatorId) return null;
            return state.callIdByElevatorId[elevatorId] || null;
        }
    );

export const getVideoCalls =
    createSelector(getEcsState,
        state => state.videoCalls || {}
    );

//
// When retrieving a list of video calls, the nextToken will be set
// to a non-null value when there are more items to fetch. Then we
// pass this nextToken value to the next list call to get the next
// batch of items. nextToken will be null when we have all the rows
//
export const getNextToken =
    createSelector(
        getEcsState,
        state => state.nextToken
    );

//
// Returns the timestamp when we started calling the given elevator;
// or undefined if there are no unresolved calls to the elevator.
//
export const getCallingElevator =
    createSelector(
        getEcsState,
        getElevatorId,
        (state, elevatorId) => (elevatorId && state.callingElevator[elevatorId]) || null
    );
    
export const getLastUpdated =
    createSelector(getEcsState,
        state => state.lastUpdated
    );

// Can look up the video call by callId or by deviceId
export const getVideoCall =
    createSelector(
        getEcsState,
        getElevatorVideoCallId,
        getCallId,
        (state, callIdFromElevatorId, callId) => {
            return ((callId || callIdFromElevatorId) && state.videoCalls[callId || callIdFromElevatorId!]) || null;
        }
    );

export const isLoadingCalls =
    createSelector(
        getEcsState,
        state => state.isLoading
    );

export const getVideoCallEvents =
    createSelector(
        getEcsState,
        getCallId,
        getFramesParameter,
        (state, callId, frames) => (callId && (frames ? state.eventsWithFrames[callId] : state.events[callId])) || null
    );

export const isLoadingEvents =
    createSelector(
        getEcsState,
        getCallId,
        (state, callId) => (callId && state.isLoadingEvents[callId]) || false
    );

export const getVideoCallEventsError =
    createSelector(
        getEcsState,
        state => state.eventsError
    );
